/**
 * Phase 5 — OVC Buffet block (per-meal theme).
 *
 * Three sub-blocks (BREAKFAST | LUNCH | DINNER). Each sub-block has:
 *   - Theme picker (select from lib_theme_buffets, or "Other…" → free text)
 *   - Three textareas: Hot Kitchen | Cold Kitchen | Pastry (pastry gets a
 *     light yellow background)
 *   - Live Station toggle + name input → when ON, the row gets a light red
 *     bg with a "LIVE STATION:" prefix
 *
 * Theme auto-fill: on theme select, IF all three dish arrays for that meal
 * are empty, populate from theme.{hot_kitchen, cold_kitchen, pastry}. If any
 * have content, just snapshot the theme name + id without overwriting.
 */
import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Flame, Plus, X } from "lucide-react";
import FestivalsEditor from "@/components/mealplan/FestivalsEditor";
// Phase 7++++ — buffet contexts now use Dish + BuffetStation objects
// with per-dish material tagging. The two compact editors live in the
// same folder and are shared with the library form dialog so the UX is
// identical inside and outside the meal-plan editor.
import DishListEditor from "@/components/mealplan/DishListEditor";
import StationsEditor from "@/components/mealplan/StationsEditor";
import SpecialPickerWithGroup from "@/components/mealplan/SpecialPickerWithGroup";

const NONE = "__none__";
const OTHER = "__other__";

// Phase 7+ / Phase 13 — meal labels for the per-meal sub-block headers.
// All three embarkation slots are Day-1-only; the parent caller decides
// whether to surface them via `meals` prop.
const MEAL_HEADER_LABEL = {
  embarkation_breakfast: "Embarkation Breakfast",
  breakfast: "Breakfast",
  brunch: "Brunch",
  lunch: "Lunch",
  embarkation_lunch: "Embarkation Lunch",
  tea_time: "Tea Time",
  embarkation_dinner: "Embarkation Dinner",
  dinner: "Dinner",
};

const emptyBlock = () => ({
  theme_buffet_id: null,
  theme_buffet_name: "",
  hot_kitchen: [],
  cold_kitchen: [],
  pastry: [],
  lines: [],
  live_station: {
    enabled: false, live_station_id: null, name: "",
    description: "", dishes: [],
  },
  festivals: [],
});

const emptyOvc = () => ({
  breakfast: emptyBlock(),
  lunch: emptyBlock(),
  dinner: emptyBlock(),
});

// Helper — derive the picker mode (select | custom) from a saved
// live-station / theme entry. Centralises the previously-nested ternary
// expression used by both `LiveStationPicker` and `MealBlock`.
function _initialPickerMode(savedId, savedName) {
  if (savedId) return "select";
  if (savedName) return "custom";
  return "select";
}

function LiveStationPicker({ value, onChange, stations, materialsLib, meal, ovcId }) {
  const v = value || {
    enabled: false, live_station_id: null, name: "",
    description: "", dishes: [],
  };
  const initialMode = _initialPickerMode(v.live_station_id, v.name);
  const [mode, setMode] = useState(initialMode);
  useEffect(() => {
    if (v.live_station_id) setMode("select");
    else if (v.name) setMode("custom");
  }, [v.live_station_id, v.name]);

  const enabled = !!v.enabled;
  // Phase 7+ — when picking from the lib, snapshot description + dishes
  // ONLY when the per-meal block hasn't typed values in. Preserves user
  // edits across re-selects.
  const pickFromLib = (id) => {
    if (id === OTHER) {
      setMode("custom");
      return onChange({
        ...v, live_station_id: null, name: "",
        description: "", dishes: [],
      });
    }
    const s = (stations || []).find((x) => x.id === id);
    const next = {
      ...v,
      live_station_id: id,
      name: s?.name || "",
    };
    if (!(v.description || "").trim()) {
      next.description = s?.description || "";
    }
    if (!(v.dishes || []).length) {
      // Phase 7++++ — lib dishes are now Dish dicts. Pass-through copy
      // (still handles legacy List[str] safely just in case).
      next.dishes = (s?.dishes || []).map((d) =>
        typeof d === "string"
          ? { name: d, material_id: null, material_name: "" }
          : {
              name: d.name || "",
              material_id: d.material_id || null,
              material_name: d.material_name || "",
            },
      );
    }
    onChange(next);
  };

  const tidPrefix = ovcId ? `${ovcId}-${meal}` : `ovc-${meal}`;
  return (
    <div
      className={cn(
        "mt-2 rounded-md border px-2.5 py-2",
        enabled ? "border-red-300 bg-red-100/60" : "border-gray-200 bg-white"
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Flame
            className={cn(
              "h-3 w-3",
              enabled ? "text-red-600" : "text-muted-foreground"
            )}
          />
          <span
            className={cn(
              "text-[10px] font-bold uppercase tracking-wider",
              enabled ? "text-red-800" : "text-muted-foreground"
            )}
          >
            Live Station{enabled && v.name ? `: ${v.name}` : ""}
          </span>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={(c) => onChange({ ...v, enabled: c })}
          data-testid={`${tidPrefix}-livestation-toggle`}
        />
      </div>
      {enabled && (
        <div className="mt-1.5 space-y-1.5">
          {mode === "select" ? (
            <Select
              value={v.live_station_id || ""}
              onValueChange={pickFromLib}
            >
              <SelectTrigger
                className="h-7 text-xs"
                data-testid={`${tidPrefix}-livestation-select`}
              >
                <SelectValue placeholder="Pick station" />
              </SelectTrigger>
              <SelectContent>
                {(stations || []).map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
                <SelectItem value={OTHER}>Other…</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <div className="flex gap-1.5">
              <Input
                value={v.name || ""}
                onChange={(e) => onChange({ ...v, live_station_id: null, name: e.target.value })}
                placeholder="Custom station name"
                className="h-7 text-xs"
                data-testid={`${tidPrefix}-livestation-name`}
              />
              <button
                type="button"
                onClick={() => { setMode("select"); onChange({ ...v, live_station_id: null, name: "" }); }}
                className="text-[10px] text-muted-foreground hover:underline"
              >List</button>
            </div>
          )}
          {/* Phase 7+ — description + dishes. Visible whenever LS is on. */}
          <Input
            value={v.description || ""}
            onChange={(e) => onChange({ ...v, description: e.target.value })}
            placeholder="Description (optional)"
            className="h-7 text-xs"
            data-testid={`${tidPrefix}-livestation-description`}
          />
          {/* Phase 7++++ — dishes are now Dish objects with material picker. */}
          <DishListEditor
            value={v.dishes || []}
            onChange={(next) => onChange({ ...v, dishes: next })}
            materialsLib={materialsLib}
            tidPrefix={`${tidPrefix}-livestation-dishes`}
            compact
          />
        </div>
      )}
    </div>
  );
}


/**
 * Phase 7+ — Festivals sub-section.
 *
 * Phase 7++: the editor moved to `FestivalsEditor.js` so regular +
 * specialty restaurant blocks can reuse it. This thin wrapper just
 * threads the per-meal `tidPrefix` the buffet block expects.
 */
function FestivalsSection({
  value, onChange, festivalsLib, meal, ovcId,
}) {
  const tidPrefix = ovcId ? `${ovcId}-${meal}` : `ovc-${meal}`;
  return (
    <FestivalsEditor
      value={value}
      onChange={onChange}
      festivalsLib={festivalsLib}
      tidPrefix={tidPrefix}
    />
  );
}

// Helper — meal-header colour class. Replaces a 4-level nested ternary
// inside the MealBlock JSX. Defaulting to "text-slate-700" keeps any
// future meal slot rendering correctly until a colour is assigned here.
const _MEAL_HEADER_COLOR_CLASS = {
  embarkation_lunch:     "text-orange-800",
  embarkation_breakfast: "text-orange-800",
  embarkation_dinner:    "text-orange-800",
  tea_time:              "text-emerald-800",
  brunch:                "text-sky-800",
};
const _mealHeaderColorClass = (meal) =>
  _MEAL_HEADER_COLOR_CLASS[meal] || "text-slate-700";

// Helper — buffet-line type → Select picker value. The picker uses
// "{kind}:{id}" so we can distinguish library `live` vs `standard`
// vs the always-allowed `custom` sentinel.
const _linePickerValue = (stationType, stationId) => {
  if (stationType === "live") return `live:${stationId}`;
  if (stationType === "standard") return `standard:${stationId}`;
  return "custom";
};

// Helper — visual chip/card class for a buffet line, keyed by
// station_type. Centralises the colour mapping so the chip + card
// stay in lock-step (three classes per type) and any future tweak
// (e.g. introducing a new "themed" type) is a single object entry.
const _LINE_VISUAL_BY_TYPE = {
  live: {
    chip: "border-red-300 bg-red-50 text-red-800",
    label: "Live",
    card: "border-red-300 bg-red-50/40",
  },
  standard: {
    chip: "border-slate-300 bg-slate-50 text-slate-700",
    label: "Standard",
    card: "border-slate-300 bg-slate-50/60",
  },
  custom: {
    chip: "border-emerald-300 bg-emerald-50 text-emerald-800",
    label: "Custom",
    card: "border-emerald-200 bg-white",
  },
};
const _lineVisual = (stationType) =>
  _LINE_VISUAL_BY_TYPE[stationType] || _LINE_VISUAL_BY_TYPE.custom;

function MealBlock({
  meal, value, onChange, themeBuffetsLib, liveStationsLib, standardStationsLib,
  materialsLib, festivalsLib, ovcId,
  // Phase 14 — ship-buffet-line list (pass-through to StationsEditor).
  linesLib,
  // Phase 16 (F5/CO1/F13) — Pizza/Carving catalogs + bundles (pass-through).
  pizzasLib, pizzaSetsLib,
  carvingsLib, carvingSetsLib,
  // Phase 16 (F10/F11/CO4) — Chef's + Bakery specials + their groups.
  chefsSpecialsLib, chefsSpecialsGroupsLib,
  bakerySpecialsLib, bakerySpecialsGroupsLib,
}) {
  const v = value || emptyBlock();
  const initialThemeMode = _initialPickerMode(v.theme_buffet_id, v.theme_buffet_name);
  const [themeMode, setThemeMode] = useState(initialThemeMode);
  useEffect(() => {
    if (v.theme_buffet_id) setThemeMode("select");
    else if (v.theme_buffet_name) setThemeMode("custom");
  }, [v.theme_buffet_id, v.theme_buffet_name]);

  // Hotfix 1 — user-intent flag to prevent any auto-stock-from-theme code
  // from undoing a manual X-delete. Flipped to `true` on any user-driven
  // change to hot_kitchen / cold_kitchen / pastry / lines. The flag
  // persists for the lifetime of this MealBlock mount (day swap remounts
  // it, theme change resets it via `pickTheme`).
  const userTouchedRef = useRef(false);
  const markUserTouched = () => { userTouchedRef.current = true; };

  const update = (patch) => onChange({ ...v, ...patch });

  const pickTheme = (id) => {
    if (id === NONE) {
      update({ theme_buffet_id: null, theme_buffet_name: "" });
      return;
    }
    if (id === OTHER) {
      setThemeMode("custom");
      update({ theme_buffet_id: null, theme_buffet_name: "" });
      return;
    }
    const theme = (themeBuffetsLib || []).find((t) => t.id === id);
    if (!theme) return;

    const allEmpty =
      (v.hot_kitchen || []).length === 0 &&
      (v.cold_kitchen || []).length === 0 &&
      (v.pastry || []).length === 0 &&
      (v.lines || []).length === 0;
    const patch = {
      theme_buffet_id: id,
      theme_buffet_name: theme.name,
    };
    // Hotfix 1 — only auto-stock when the user has NEVER manually
    // touched the station lists in this MealBlock session. Once they
    // X-delete or add anything by hand, the theme picker only updates
    // name/snapshot fields, never re-fills hot/cold/pastry/lines.
    // Prevents the "auto-repopulate after delete" regression.
    const safeToAutoStock = allEmpty && !userTouchedRef.current;
    if (safeToAutoStock) {
      // Phase 7++++ — theme.hot/cold/pastry are now List[BuffetStation],
      // and theme.lines[].items are now List[Dish]. Deep-copy each
      // station + dish so editing the meal block doesn't mutate the
      // library snapshot in place.
      const cloneStations = (arr) =>
        (arr || []).map((s) => ({
          name: s.name || "",
          description: s.description || "",
          dishes: (s.dishes || []).map((d) =>
            typeof d === "string"
              ? { name: d, material_id: null, material_name: "" }
              : {
                  name: d.name || "",
                  material_id: d.material_id || null,
                  material_name: d.material_name || "",
                },
          ),
          station_type: s.station_type || "custom",
          station_id: s.station_id || null,
        }));
      const cloneDishes = (arr) =>
        (arr || []).map((d) =>
          typeof d === "string"
            ? { name: d, material_id: null, material_name: "" }
            : {
                name: d.name || "",
                material_id: d.material_id || null,
                material_name: d.material_name || "",
              },
        );
      patch.hot_kitchen = cloneStations(theme.hot_kitchen);
      patch.cold_kitchen = cloneStations(theme.cold_kitchen);
      patch.pastry = cloneStations(theme.pastry);
      patch.lines = (theme.lines || [])
        .filter((ln) => (ln.name || "").trim())
        .map((ln) => ({
          name: ln.name,
          items: cloneDishes(ln.items),
        }));
    }
    update(patch);
  };

  const lines = Array.isArray(v.lines) ? v.lines : [];
  const updateLine = (idx, patch) =>
    update({
      lines: lines.map((ln, i) => (i === idx ? { ...ln, ...patch } : ln)),
    });
  const removeLine = (idx) =>
    update({ lines: lines.filter((_, i) => i !== idx) });
  // Phase 7+++ — new lines default to station_type=custom. Picker turns
  // them into Live or Standard refs (snapshotting name/desc/dishes).
  const addLine = () =>
    update({
      lines: [
        ...lines,
        {
          name: "",
          items: [],
          description: "",
          station_type: "custom",
          station_id: null,
        },
      ],
    });

  // Phase 7+++ — pick a station from Live or Standard library.
  // Snapshot name/description/items into the line, preserving user edits
  // when the local field already has content.
  const applyStationPick = (idx, kind, libDoc) => {
    if (!libDoc) {
      updateLine(idx, { station_type: "custom", station_id: null });
      return;
    }
    const cur = lines[idx] || {};
    updateLine(idx, {
      station_type: kind,
      station_id: libDoc.id,
      name: (cur.name || "").trim() || libDoc.name || "",
      description:
        (cur.description || "").trim() || libDoc.description || "",
      items:
        (cur.items || []).length > 0
          ? cur.items
          : [...(libDoc.dishes || [])],
    });
  };

  return (
    <div className="rounded-md border bg-slate-50/40 p-3" data-testid={`ovc-block-${meal}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h4 className={cn(
          "text-[11px] font-bold uppercase tracking-widest",
          _mealHeaderColorClass(meal),
        )}>
          {MEAL_HEADER_LABEL[meal] || meal}
          {v.theme_buffet_name && (
            <span className="ml-2 text-[10px] font-medium normal-case text-slate-500">
              — Theme: <em className="not-italic font-semibold">{v.theme_buffet_name}</em>
            </span>
          )}
        </h4>
      </div>

      {/* Theme picker */}
      <div className="mb-2">
        {themeMode === "select" ? (
          <Select value={v.theme_buffet_id || ""} onValueChange={pickTheme}>
            <SelectTrigger
              className="h-7 text-xs"
              data-testid={`ovc-${meal}-theme-select`}
            >
              <SelectValue placeholder="Pick a theme buffet…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>— None —</SelectItem>
              {(themeBuffetsLib || []).map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
              <SelectItem value={OTHER}>Other…</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <div className="flex gap-1.5">
            <Input
              value={v.theme_buffet_name || ""}
              onChange={(e) =>
                update({ theme_buffet_id: null, theme_buffet_name: e.target.value })
              }
              placeholder="Custom theme name"
              className="h-7 text-xs"
              data-testid={`ovc-${meal}-theme-custom`}
            />
            <button
              type="button"
              onClick={() => { setThemeMode("select"); update({ theme_buffet_id: null, theme_buffet_name: "" }); }}
              className="text-[10px] text-muted-foreground hover:underline"
            >List</button>
          </div>
        )}
      </div>

      {/* Phase 16 (F14) — Origin field. Sits right under the theme
          picker (per spec: "next to the name"). Auto-fills on backend
          when theme picked AND user hasn't typed an override. */}
      <div className="mb-2 flex items-center gap-1.5">
        <Label className="shrink-0 text-[9px] font-semibold uppercase tracking-wider text-slate-600">
          Origin
        </Label>
        <Input
          value={v.origin ?? ""}
          onChange={(e) => update({ origin: e.target.value })}
          placeholder={(() => {
            const tb = (themeBuffetsLib || []).find((t) => t.id === v.theme_buffet_id);
            const def = tb?.default_origin;
            return def ? `Default: ${def}` : "e.g. Italian, Greek, Mediterranean";
          })()}
          className="h-7 flex-1 text-[11px] italic"
          data-testid={`ovc-${meal}-origin`}
        />
      </div>

      {/* Phase 16 (F6) — Soup of the Day. Pre-filled from the theme's
          `default_soup_of_the_day` on the BACKEND when a theme is picked
          and the per-day field is None. Once user types here the value
          sticks per-day forever (server treats `""` as explicit clear). */}
      <div className="mb-2 flex items-center gap-1.5">
        <Label className="shrink-0 text-[9px] font-semibold uppercase tracking-wider text-slate-600">
          Soup of the Day
        </Label>
        <Input
          value={v.soup_of_the_day ?? ""}
          onChange={(e) => update({ soup_of_the_day: e.target.value })}
          placeholder={(() => {
            const tb = (themeBuffetsLib || []).find((t) => t.id === v.theme_buffet_id);
            const def = tb?.default_soup_of_the_day;
            return def ? `Default: ${def}` : "e.g. Minestrone";
          })()}
          className="h-7 flex-1 text-[11px] italic"
          data-testid={`ovc-${meal}-soup-of-the-day`}
        />
      </div>

      {/* Phase 16 (F10/F11/CO4) — Chef's Special + Bakery Special on
          OVC buffet meal blocks, each with an optional group filter.
          Backend: chefs_special_id/name/group_id + bakery_*_*. */}
      <SpecialPickerWithGroup
        label="Chef's Special"
        valueId={v.chefs_special_id || null}
        valueName={v.chefs_special_name || null}
        groupId={v.chefs_special_group_id || null}
        itemsLib={chefsSpecialsLib}
        groupsLib={chefsSpecialsGroupsLib}
        onChange={({ id, name, group_id }) =>
          update({
            chefs_special_id: id,
            chefs_special_name: name,
            chefs_special_group_id: group_id,
          })
        }
        tidPrefix={`ovc-${meal}-chefs-special`}
        accent="amber"
      />
      <SpecialPickerWithGroup
        label="Bakery Special"
        valueId={v.bakery_special_id || null}
        valueName={v.bakery_special_name || null}
        groupId={v.bakery_special_group_id || null}
        itemsLib={bakerySpecialsLib}
        groupsLib={bakerySpecialsGroupsLib}
        onChange={({ id, name, group_id }) =>
          update({
            bakery_special_id: id,
            bakery_special_name: name,
            bakery_special_group_id: group_id,
          })
        }
        tidPrefix={`ovc-${meal}-bakery-special`}
        accent="rose"
      />

      {/* Phase 7++++ — Hot / Cold / Pastry as list of Station cards.
          Each station has name + description + dishes (with material) and
          can pull a snapshot from lib_live_stations or lib_standard_stations. */}
      <div className="grid grid-cols-1 gap-2 lg:grid-cols-3">
        <StationsEditor
          value={v.hot_kitchen || []}
          onChange={(next) => { markUserTouched(); update({ hot_kitchen: next }); }}
          liveStationsLib={liveStationsLib}
          standardStationsLib={standardStationsLib}
          materialsLib={materialsLib}
          linesLib={linesLib}
          pizzasLib={pizzasLib}
          pizzaSetsLib={pizzaSetsLib}
          carvingsLib={carvingsLib}
          carvingSetsLib={carvingSetsLib}
          tidPrefix={`ovc-${meal}-hot`}
          accent="orange"
        />
        <StationsEditor
          value={v.cold_kitchen || []}
          onChange={(next) => { markUserTouched(); update({ cold_kitchen: next }); }}
          liveStationsLib={liveStationsLib}
          standardStationsLib={standardStationsLib}
          materialsLib={materialsLib}
          linesLib={linesLib}
          pizzasLib={pizzasLib}
          pizzaSetsLib={pizzaSetsLib}
          carvingsLib={carvingsLib}
          carvingSetsLib={carvingSetsLib}
          tidPrefix={`ovc-${meal}-cold`}
          accent="cyan"
        />
        <StationsEditor
          value={v.pastry || []}
          onChange={(next) => { markUserTouched(); update({ pastry: next }); }}
          liveStationsLib={liveStationsLib}
          standardStationsLib={standardStationsLib}
          materialsLib={materialsLib}
          linesLib={linesLib}
          pizzasLib={pizzasLib}
          pizzaSetsLib={pizzaSetsLib}
          carvingsLib={carvingsLib}
          carvingSetsLib={carvingSetsLib}
          tidPrefix={`ovc-${meal}-pastry`}
          accent="yellow"
        />
      </div>

      {/* Buffet Lines (Phase 5 refinement) — dynamic named stations under
          hot/cold/pastry. Each line has a name + items textarea. */}
      <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50/40 p-2">
        <div className="mb-1 flex items-center justify-between">
          <Label className="text-[10px] font-bold uppercase tracking-widest text-emerald-900">
            Buffet Lines
          </Label>
          <span className="text-[9px] text-emerald-800/70">
            Named buffet stations. Will print as extra sub-cells on the PDF.
          </span>
        </div>
        {lines.length === 0 && (
          <p className="text-[10px] italic text-muted-foreground">
            No lines yet — click “+ Add line” to add one.
          </p>
        )}
        <div className="space-y-1.5">
          {lines.map((ln, idx) => {
            const stationType = ln.station_type || "custom";
            const stationId = ln.station_id || "";
            const pickerValue = _linePickerValue(stationType, stationId);
            const onPickerChange = (val) => {
              if (val === "custom") {
                applyStationPick(idx, "custom", null);
                return;
              }
              const [kind, id] = val.split(":");
              const lib = kind === "live"
                ? (liveStationsLib || []).find((s) => s.id === id)
                : (standardStationsLib || []).find((s) => s.id === id);
              if (lib) applyStationPick(idx, kind, lib);
            };
            const visual = _lineVisual(stationType);
            const typeChipClass = visual.chip;
            const typeChipLabel = visual.label;
            const cardBgClass = visual.card;
            return (
              <div
                key={idx}
                className={`rounded border p-1.5 ${cardBgClass}`}
                data-testid={`ovc-${meal}-line-${idx}`}
              >
                <div className="mb-1 flex items-center gap-1.5">
                  <Select value={pickerValue} onValueChange={onPickerChange}>
                    <SelectTrigger
                      className="h-7 flex-1 text-[10px]"
                      data-testid={`ovc-${meal}-line-${idx}-station-select`}
                    >
                      <SelectValue placeholder="Pick station type…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">— Custom (no library) —</SelectItem>
                      {(liveStationsLib || []).length > 0 && (
                        <div className="px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-red-700">
                          Live Stations
                        </div>
                      )}
                      {(liveStationsLib || []).map((s) => (
                        <SelectItem key={`live:${s.id}`} value={`live:${s.id}`}>{s.name}</SelectItem>
                      ))}
                      {(standardStationsLib || []).length > 0 && (
                        <div className="px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-slate-700">
                          Standard Stations
                        </div>
                      )}
                      {(standardStationsLib || []).map((s) => (
                        <SelectItem key={`standard:${s.id}`} value={`standard:${s.id}`}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span
                    className={`shrink-0 rounded border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${typeChipClass}`}
                    data-testid={`ovc-${meal}-line-${idx}-type`}
                  >
                    {typeChipLabel}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeLine(idx)}
                    className="shrink-0 rounded p-1 text-red-600 hover:bg-red-50"
                    aria-label="Remove line"
                    data-testid={`ovc-${meal}-line-remove-${idx}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
                <Input
                  value={ln.name || ""}
                  onChange={(e) =>
                    updateLine(idx, { name: e.target.value })
                  }
                  placeholder="Station name"
                  className="h-7 text-xs"
                  data-testid={`ovc-${meal}-line-name-${idx}`}
                />
                <Input
                  value={ln.description || ""}
                  onChange={(e) =>
                    updateLine(idx, { description: e.target.value })
                  }
                  placeholder="Description (optional, shows above dishes on PDF)"
                  className="mt-1 h-7 text-[11px] italic"
                  data-testid={`ovc-${meal}-line-${idx}-description`}
                />
                <DishListEditor
                  value={ln.items || []}
                  onChange={(next) => updateLine(idx, { items: next })}
                  materialsLib={materialsLib}
                  tidPrefix={`ovc-${meal}-line-items-${idx}`}
                  compact
                />
              </div>
            );
          })}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addLine}
          className="mt-1.5 h-6 px-2 text-[10px]"
          data-testid={`ovc-${meal}-line-add`}
        >
          <Plus className="h-3 w-3" />
          Add line
        </Button>
      </div>

      <LiveStationPicker
        value={v.live_station}
        onChange={(ls) => update({ live_station: ls })}
        stations={liveStationsLib}
        materialsLib={materialsLib}
        meal={meal}
        ovcId={ovcId}
      />

      <FestivalsSection
        value={v.festivals || []}
        onChange={(fs) => update({ festivals: fs })}
        festivalsLib={festivalsLib}
        meal={meal}
        ovcId={ovcId}
      />
    </div>
  );
}

// Phase 7+ — meal-block ordering. Embarkation Lunch (Day 1 only) renders
// FIRST, above breakfast, so the unusual times jump out. Tea Time slots
// between Lunch and Dinner. Brunch slots between Breakfast and Lunch.
const DEFAULT_MEALS = ["breakfast", "lunch", "dinner"];

export default function OvcBlock({
  value, onChange, themeBuffetsLib, liveStationsLib, standardStationsLib,
  materialsLib, festivalsLib,
  // Phase 14 — ship-buffet-line list (pass-through to MealBlock).
  linesLib,
  // Phase 16 (F5/CO1) — Pizza catalog + saved bundles.
  pizzasLib, pizzaSetsLib,
  // Phase 16 (F13) — Carving catalog + saved bundles.
  carvingsLib, carvingSetsLib,
  // Phase 16 (F10/F11/CO4) — Chef's + Bakery specials + group filters.
  chefsSpecialsLib, chefsSpecialsGroupsLib,
  bakerySpecialsLib, bakerySpecialsGroupsLib,
  // Phase 7 — `BuffetCard`-mode props. Backwards compatible: when none of
  // these are supplied this component renders as the legacy
  // "OVC Buffet — Independent themes per meal" card.
  title,
  testId = "ovc-block",
  buffetId,
  meals,
}) {
  const v = value || {};
  const updateMeal = (meal, block) => onChange({ ...v, [meal]: block });
  // Phase 13 — when the parent passes an explicit `meals` list, trust it
  // entirely (don't drop slots whose value happens to be null/missing —
  // `MealBlock` already falls back to `emptyBlock()` for those). This is
  // the path the BuffetsBlock + crew-mess use to render Day-1-only
  // embarkation slots even before the user has touched them.
  // When no `meals` prop is provided (legacy callers), keep the original
  // behaviour: filter DEFAULT_MEALS by presence in `v`, fall back to all
  // three if none are present.
  let effectiveMeals;
  if (meals && meals.length) {
    effectiveMeals = meals;
  } else {
    const filtered = DEFAULT_MEALS.filter(
      (m) => v[m] !== undefined && v[m] !== null,
    );
    effectiveMeals = filtered.length ? filtered : DEFAULT_MEALS;
  }

  return (
    <section
      className="rounded-lg border bg-card p-4"
      data-testid={testId}
    >
      <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">
        {title || "OVC Buffet — Independent themes per meal"}
      </h3>
      <div className="space-y-3">
        {effectiveMeals.map((meal) => (
          <MealBlock
            key={meal}
            meal={meal}
            value={v[meal] || emptyBlock()}
            onChange={(b) => updateMeal(meal, b)}
            themeBuffetsLib={themeBuffetsLib}
            liveStationsLib={liveStationsLib}
            standardStationsLib={standardStationsLib}
            materialsLib={materialsLib}
            festivalsLib={festivalsLib}
            ovcId={buffetId || testId}
            linesLib={linesLib}
            pizzasLib={pizzasLib}
            pizzaSetsLib={pizzaSetsLib}
            carvingsLib={carvingsLib}
            carvingSetsLib={carvingSetsLib}
            chefsSpecialsLib={chefsSpecialsLib}
            chefsSpecialsGroupsLib={chefsSpecialsGroupsLib}
            bakerySpecialsLib={bakerySpecialsLib}
            bakerySpecialsGroupsLib={bakerySpecialsGroupsLib}
          />
        ))}
      </div>
    </section>
  );
}
